import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, CalendarCheck, ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openChatStream } from "@/lib/chat";
import { QUERY_TOPICS } from "@/data/content";

const WELCOME = {
  role: "assistant",
  content:
    "Hi, I'm Ray, TWORAY's digital advisor. Ask me about our advisory services, our approach or our packages — or book a Discovery Call any time.",
};

const SUGGESTIONS = [
  "What does TWORAY do?",
  "How do your packages work?",
  "Can you help with Cyber Essentials?",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "booking" | "booked"
  const [bookingMode, setBookingMode] = useState("call"); // "call" | "query"
  const [booking, setBooking] = useState({
    name: "",
    company: "",
    email: "",
    topic: "",
    note: "",
    ack: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingTouched, setBookingTouched] = useState({});
  const [bookingSubmitAttempted, setBookingSubmitAttempted] = useState(false);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, view]);

  useEffect(() => {
    if (open && view === "chat" && inputRef.current) inputRef.current.focus();
  }, [open, view]);

  async function send(text) {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await openChatStream({
        session_id: sessionId.current,
        message: content,
      });
      const sid = res.headers.get("X-Session-Id");
      if (sid) sessionId.current = sid;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't connect. Please try again or email hello@tworay.co.uk.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const touchBookingField = (field) =>
    setBookingTouched((current) => ({ ...current, [field]: true }));

  const bookingErrors = {
    name: !booking.name.trim() ? "Please enter your name." : "",
    company:
      bookingMode === "query" && !booking.company.trim()
        ? "Please enter your company or organisation."
        : "",
    email: !booking.email.trim()
      ? "Please enter your work email address."
      : !EMAIL_RE.test(booking.email.trim())
        ? "Please enter a valid email address."
        : "",
    topic:
      bookingMode === "query" && !booking.topic
        ? "Please select a query topic."
        : "",
    note:
      bookingMode === "query" && !booking.note.trim()
        ? "Please enter your question or requirement."
        : "",
    ack: !booking.ack
      ? "Please accept the Privacy Policy to continue."
      : "",
  };

  const showBookingError = (field) =>
    (bookingSubmitAttempted || bookingTouched[field]) &&
    bookingErrors[field];

  async function submitBooking() {
    setBookingError("");
    setBookingSubmitAttempted(true);

    const firstError = Object.values(bookingErrors).find(Boolean);
    if (firstError) {
      setBookingError("Please check the highlighted fields below.");
      return;
    }

    const currentUrl =
      typeof window !== "undefined" ? new URL(window.location.href) : null;

    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: booking.name.trim(),
        company: bookingMode === "query" ? booking.company.trim() : "",
        email: booking.email.trim(),
        message: booking.note.trim(),
        service_interest:
          bookingMode === "query" ? booking.topic : "",
        preferred_contact: "Email",
        privacy_ack: true,
        lead_channel: "Ray",
        enquiry_type:
          bookingMode === "query"
            ? "Ray Advisory Query"
            : "Discovery Call",
        landing_page: currentUrl?.href || "",
        referrer:
          typeof document !== "undefined" ? document.referrer : "",
        utm_source: currentUrl?.searchParams.get("utm_source") || "",
        utm_medium: currentUrl?.searchParams.get("utm_medium") || "",
        utm_campaign: currentUrl?.searchParams.get("utm_campaign") || "",
        utm_content: currentUrl?.searchParams.get("utm_content") || "",
        source: "chat",
        session_id: sessionId.current || "",
      });
      setView("booked");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            bookingMode === "query"
              ? `Thanks ${booking.name.trim().split(" ")[0]} — your query has been sent. The TWORAY team will reply within one business day at ${booking.email.trim()}.`
              : `Thanks ${booking.name.trim().split(" ")[0]} — that's booked. The TWORAY team will be in touch within one business day at ${booking.email.trim()}.`,
        },
      ]);
    } catch (e) {
      setBookingError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  const bInput = "w-full rounded-lg border border-brandborder bg-lightbg px-3 py-2 text-sm text-navy outline-none focus:border-brandblue transition-colors";

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label={open ? "Close chat" : "Chat with Ray, TWORAY's AI advisory assistant"}
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-xl transition-transform duration-200 hover:scale-105 hover:bg-brandblue focus:outline-none focus:ring-2 focus:ring-cyan"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[60] flex w-[calc(100vw-3rem)] max-w-[380px] flex-col overflow-hidden rounded-xl border border-brandborder bg-white shadow-2xl"
          style={{ height: "min(580px, calc(100vh - 8rem))" }}
        >
          <div className="flex items-center gap-3 bg-navy px-5 py-4 text-white">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan text-navy font-bold">R</div>
            <div>
              <div className="text-sm font-semibold leading-tight">Ray · AI Advisory Assistant</div>
              <div className="text-xs text-white/60">Instant automated guidance — not human live chat</div>
            </div>
          </div>

          {/* Booking view */}
          {view === "booking" ? (
            <div className="flex-1 overflow-y-auto bg-lightbg p-5" data-testid="chat-booking-form">
              <button onClick={() => setView("chat")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate hover:text-brandblue transition-colors" data-testid="booking-back">
                <ArrowLeft size={15} /> Back to chat
              </button>
              <h3 className="font-heading font-bold text-lg text-navy">{bookingMode === "query" ? "Send an advisory query" : "Book a Discovery Call"}</h3>
              <p className="mt-1 text-sm text-slate">{bookingMode === "query" ? "Leave your question and we'll reply within one business day." : "Leave your details and we'll be in touch within one business day."}</p>
              <div className="mt-4 space-y-3">
                <input
                  className={bInput}
                  placeholder="Your name"
                  value={booking.name}
                  onChange={(e) =>
                    setBooking((b) => ({ ...b, name: e.target.value }))
                  }
                  onBlur={() => touchBookingField("name")}
                  aria-invalid={Boolean(showBookingError("name"))}
                  data-testid="booking-name"
                />
                {showBookingError("name") && (
                  <p className="text-xs text-destructive">{bookingErrors.name}</p>
                )}

                {bookingMode === "query" && (
                  <>
                    <input
                      className={bInput}
                      placeholder="Company / Organisation"
                      value={booking.company}
                      onChange={(e) =>
                        setBooking((b) => ({
                          ...b,
                          company: e.target.value,
                        }))
                      }
                      onBlur={() => touchBookingField("company")}
                      aria-invalid={Boolean(showBookingError("company"))}
                      data-testid="booking-company"
                    />
                    {showBookingError("company") && (
                      <p className="text-xs text-destructive">
                        {bookingErrors.company}
                      </p>
                    )}
                  </>
                )}

                <input
                  className={bInput}
                  placeholder="Work email"
                  value={booking.email}
                  onChange={(e) =>
                    setBooking((b) => ({ ...b, email: e.target.value }))
                  }
                  onBlur={() => touchBookingField("email")}
                  aria-invalid={Boolean(showBookingError("email"))}
                  data-testid="booking-email"
                />
                {showBookingError("email") && (
                  <p className="text-xs text-destructive">{bookingErrors.email}</p>
                )}

                {bookingMode === "query" && (
                  <>
                    <select
                      className={bInput}
                      value={booking.topic}
                      onChange={(e) =>
                        setBooking((b) => ({
                          ...b,
                          topic: e.target.value,
                        }))
                      }
                      onBlur={() => touchBookingField("topic")}
                      aria-invalid={Boolean(showBookingError("topic"))}
                      data-testid="booking-topic"
                    >
                      <option value="">Query topic</option>
                      {QUERY_TOPICS.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                    {showBookingError("topic") && (
                      <p className="text-xs text-destructive">
                        {bookingErrors.topic}
                      </p>
                    )}
                  </>
                )}

                <textarea
                  className={`${bInput} min-h-[72px] resize-none`}
                  placeholder={
                    bookingMode === "query"
                      ? "Your question or requirement"
                      : "What would you like to discuss? (optional)"
                  }
                  value={booking.note}
                  onChange={(e) =>
                    setBooking((b) => ({ ...b, note: e.target.value }))
                  }
                  onBlur={() => touchBookingField("note")}
                  aria-invalid={Boolean(showBookingError("note"))}
                  data-testid="booking-note"
                />
                {showBookingError("note") && (
                  <p className="text-xs text-destructive">{bookingErrors.note}</p>
                )}
                <div>
                  <label className="flex items-start gap-2 text-xs text-slate">
                    <input
                      type="checkbox"
                      checked={booking.ack}
                      onChange={(e) => {
                        setBooking((b) => ({ ...b, ack: e.target.checked }));
                        touchBookingField("ack");
                      }}
                      className="mt-0.5 h-4 w-4 accent-[#0968F0]"
                      data-testid="booking-ack"
                    />
                    <span>I agree to TWORAY contacting me about my enquiry, per the <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-brandblue font-medium">Privacy Policy</a>.</span>
                  </label>
                  {showBookingError("ack") && (
                    <p className="mt-1 text-xs text-destructive">{bookingErrors.ack}</p>
                  )}
                </div>
                {bookingError && <p className="text-xs text-destructive" data-testid="booking-error">{bookingError}</p>}
                <button onClick={submitBooking} disabled={submitting} data-testid="booking-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-navy py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brandblue disabled:opacity-50">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} {submitting ? (bookingMode === "query" ? "Sending…" : "Booking…") : (bookingMode === "query" ? "Send my query" : "Request my call")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 border-b border-brandborder bg-white px-3 py-2.5" data-testid="chat-choice-bar">
                <button onClick={() => { setBookingMode("call"); setView("booking"); }} data-testid="chat-choice-call" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brandblue">
                  <CalendarCheck size={14} /> Book a call
                </button>
                <button onClick={() => { setBookingMode("query"); setView("booking"); }} data-testid="chat-choice-query" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-brandborder px-3 py-2 text-xs font-semibold text-navy transition-colors hover:border-brandblue hover:text-brandblue">
                  <Mail size={14} /> Send a query
                </button>
              </div>

              <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto bg-lightbg px-4 py-5">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      data-testid={`chat-msg-${m.role}`}
                      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user" ? "rounded-br-sm bg-brandblue text-white" : "rounded-bl-sm bg-white text-navy shadow-sm"
                      }`}
                    >
                      {m.content || (sending && i === messages.length - 1 ? <Loader2 size={16} className="animate-spin text-slate" /> : "")}
                    </div>
                  </div>
                ))}

                {messages.length === 1 && (
                  <div className="space-y-2 pt-1">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="block w-full rounded-lg border border-brandborder bg-white px-3 py-2 text-left text-sm text-navy transition-colors hover:border-brandblue hover:text-brandblue">
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {view === "booked" ? (
                <div className="flex items-center gap-2 border-t border-brandborder bg-iceblue px-4 py-3 text-sm font-medium text-brandblue" data-testid="chat-booked-banner">
                  <CheckCircle2 size={16} /> {bookingMode === "query" ? "Query sent — we'll be in touch." : "Discovery Call requested — we'll be in touch."}
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-2 border-t border-brandborder bg-white p-3">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      rows={1}
                      placeholder="Ask about TWORAY…"
                      data-testid="chat-input"
                      className="max-h-28 flex-1 resize-none rounded-lg border border-brandborder bg-lightbg px-3 py-2 text-sm text-navy outline-none focus:border-brandblue"
                    />
                    <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="chat-send" aria-label="Send message" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-white transition-colors hover:bg-brandblue disabled:opacity-40">
                      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                  <p className="px-3 pb-2.5 text-[10px] leading-snug text-slate" data-testid="chat-privacy-note">
                    Ray is an automated AI assistant. Please don't share sensitive personal data here. See our <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-brandblue hover:underline">Privacy Policy</a>.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
